<template>
  <div ref="documentsViewRef" v-editable="props.stepData" class="step-documents-view">
    <div v-if="props.stepData.enableSignature" id="signatur">
      <PflegeDivider border-color="mint-dark" icon="bulk-edit-line" />

      <ErrorMessage
        error-message="Klicken Sie hier, um Ihre digitale Unterschrift zu erstellen."
        :has-error="!!validationErrors.signature && showErrorMessages"
      />
      <div class="signature-button">
        <PflegeButton
          :class="{ 'signature-success': !!actualStepdataRelation.data.signatur }"
          :label="
            !!actualStepdataRelation.data.signatur
              ? 'Digitale Unterschrift erstellt'
              : 'Digitale Unterschrift erstellen'
          "
          :icon="!!actualStepdataRelation.data.signatur ? 'icon-success' : 'arrow-right-line'"
          @click="showSignatureModal = true"
        />
      </div>
    </div>

    <div class="documents-wrapper mint-lighter">
      <Document
        v-for="(document, index) in props.stepData.documents"
        :key="document._uid"
        :blok="document"
        :loading="isDocumentLoading"
        :link="wizco.documentLinks?.[document.type]"
        :errors="validationErrors"
        :show-error-messages
        @document-click="onDocumentClick(index)"
        @update-document-data="validate"
      />
      <WizcoRichtextResolver :blok="props.stepData.footerContent" />
    </div>
    <StepFooter
      :error-message="stepFooterErrorMessage"
      :error-variant="showDownloadError ? 'warning' : 'danger'"
      @click="nextStepBtnClick"
    >
      <template #alert>
        <span class="text-bold scroll-to-error" @click="scrollToError">
          Zur fehlenden Angabe
          <PflegeIcon name="arrow-up-line" size="l" />
        </span>
      </template>
    </StepFooter>
  </div>

  <PflegeModal v-model="showSignatureModal">
    <slot name="submitModalContent">
      <PflegeSignatureInput
        v-if="showSignatureInput"
        :model-value="
          !!actualStepdataRelation.data.signatur ? actualStepdataRelation.data.signatur : undefined
        "
        @input="setSignature"
        @empty="setSignature(null)"
      />
    </slot>
  </PflegeModal>
</template>

<script setup>
import { shallowRef, watch, computed, useTemplateRef, reactive } from 'vue'
import StepFooter from '../components/footer/StepFooter.vue'
import Document from '../components/storyblok/step/Document.vue'
import { useWizcoStore } from '../stores/wizco'
import ErrorMessage from '../components/input/ErrorMessage.vue'

const props = defineProps({
  stepData: {
    type: Object,
    required: true
  },
  stepUuid: {
    type: String,
    required: false,
    default: ''
  }
})

const documentsViewRef = useTemplateRef('documentsViewRef')
const wizco = useWizcoStore()
const actualStepdataRelation = wizco.stepData[props.stepUuid]

//  Prep document step data
if (props.stepData.documents) {
  props.stepData.documents.forEach((document) => {
    if (!actualStepdataRelation.data.dokumente) {
      actualStepdataRelation.data.dokumente = []
    }

    const dataDocument = actualStepdataRelation.data.dokumente.find(
      (item) => item.type === document.type
    )
    if (!dataDocument) {
      actualStepdataRelation.data.dokumente.push({
        type: document.type
      })
    }
  })
}

//  Signature
const showSignatureModal = shallowRef(false)
const showSignatureInput = shallowRef(false)

watch(showSignatureModal, (val) => {
  if (val) {
    //  We need to wait to render the signature after the modal is rendered
    //  because the signature needs to resize depending on the with. Otherwise
    //  the signature-pad is too small.
    showSignatureInput.value = true
  } else {
    //  When the modal is closed, there is a transition we need to wait for
    //  before we unmount the signature-pad. Otherwise we have ui-flickers.
    setTimeout(() => {
      showSignatureInput.value = false
    }, 300)
  }
})

const setSignature = (svg) => {
  actualStepdataRelation.data.signatur = svg
  showSignatureModal.value = false
  validate()
}

const documentsClickStatus = props.stepData.documents.map(
  (document) => !document.isLinkClickRequired
)
const showDownloadError = shallowRef(false)
const onDocumentClick = (index) => {
  documentsClickStatus[index] = true
  validate()
}

const showErrorMessages = shallowRef(false)

//  Validation
const validationErrors = reactive({
  documents: {},
  optIns: {},
  signature: false
})

const validationErrorElementId = computed(() => {
  //  Jump to signature first
  if (!!validationErrors.signature) {
    return '#signatur'
  }

  //  Jump to first document error
  if (!!validationErrors.documents && Object.keys(validationErrors.documents).length) {
    return `#${Object.keys(validationErrors.documents)[0]}`
  }

  //  Jump to first opt-in error
  if (!!validationErrors.optIns && Object.keys(validationErrors.optIns).length) {
    return `#${Object.keys(validationErrors.optIns)[0]}`
  }

  //  Jump to top
  return null
})

const scrollToError = () => {
  const element =
    documentsViewRef.value.querySelector(validationErrorElementId.value) || documentsViewRef.value

  if (element) {
    element.scrollIntoView({ behavior: 'smooth' })
  }
}

const validate = () => {
  let isValid = true
  const stepData = props.stepData

  //  Signature
  if (stepData.enableSignature && !actualStepdataRelation.data.signatur) {
    //  Set error
    validationErrors.signature = true
    isValid = false
  } else {
    validationErrors.signature = false
  }

  //  Documents
  stepData.documents.forEach((doc) => {
    const dataDoc = actualStepdataRelation.data.dokumente.find((item) => item.type === doc.type)

    // signiert toggle
    if (doc.signDocument && !dataDoc.dokumentSigniert) {
      validationErrors.documents[doc.type] = true
      isValid = false
    } else {
      validationErrors.documents[doc.type] = false
    }

    // downloaded
    if (documentsClickStatus.every((clickStatus) => clickStatus === true)) {
      showDownloadError.value = false
    } else {
      isValid = false
      showDownloadError.value = true
    }

    //  OptIns
    if (doc.optIns) {
      doc.optIns.forEach((optIn) => {
        if (optIn.required && !dataDoc?.optIns?.[optIn.type]) {
          validationErrors.optIns[optIn.type] = true
          isValid = false
        } else {
          validationErrors.optIns[optIn.type] = false
        }
      })
    }
  })

  wizco.isActiveStepReady = isValid
}

const hasCta = props.stepData.documents.some((document) => document.linkText)
const isDocumentLoading = shallowRef(hasCta)
watch(
  () => wizco.isLoading,
  async (isLoading) => {
    if (!isLoading) {
      validate()
      if (hasCta) {
        await wizco.getDocuments(props.stepData.useCase)
        isDocumentLoading.value = false
      }
    }
  },
  { immediate: true }
)

const nextStepBtnClick = () => {
  validate()

  if (!wizco.isActiveStepReady) {
    showErrorMessages.value = true
    return
  }
  wizco.submit()
}

const stepFooterErrorMessage = computed(() => {
  if (!showErrorMessages.value) {
    return ''
  }

  if (showDownloadError.value) {
    return 'Zum Fortfahren laden Sie bitte den Antrag herunter.'
  }

  if (!wizco.isActiveStepReady) {
    return 'Antrag unvollständig. Zum Einreichen fehlt noch eine Angabe.'
  }

  return ''
})
</script>

<style scoped lang="scss">
.step-documents-view {
  --gap: 1.33rem;
}
.documents-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  padding: var(--gap);
}
.richtextresolver {
  padding: var(--gap) 1rem;
}
.richtextresolver:has(> .text:only-child > p:only-child:empty) {
  display: none;
}
.signature-button {
  text-align: center;
  padding: var(--gap);

  :deep(.base-button) {
    justify-content: center;

    button {
      &.signature-success {
        border: 1px solid var(--mint-dark);
        color: var(--black);
        background-color: var(--success-light);

        .icon {
          fill: var(--success) !important;
        }

        &:focus {
          box-shadow:
            inset 0 0 0 4px var(--mint-dark),
            inset 0 0 0 6px var(--white);
        }
      }

      background-color: var(--mint-dark);

      &:focus {
        box-shadow:
          inset 0 0 0 4px var(--mint-light),
          inset 0 0 0 6px var(--white);
      }
    }
  }
}
.scroll-to-error {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  cursor: pointer;
  color: var(--black);
  text-decoration: none;
}
</style>
